<div class="weight-report">
  <app-section-header title="{{ 'weightReport.title' | translate }}"></app-section-header>

  <!-- Job card information -->
  <mat-card class="section-card" appearance="outlined">
    <div class="job-card-header">
      <mat-card-title class="section-title">{{ 'weightReport.jobCard.title' | translate }}</mat-card-title>
      <div class="job-card-recipient">
        <label>{{ 'weightReport.documentation.recipient' | translate }}</label>
        <span>{{ recipientEmail || ('weightReport.documentation.recipientHint' | translate) }}</span>
      </div>
    </div>
    <div class="field-grid">
      <div class="readonly-field">
        <label>{{ 'weightReport.jobCard.workOrderNumber' | translate }}</label>
        <span>{{ workOrder?.workOrderNumber || '-' }}</span>
      </div>
      <div class="readonly-field">
        <label>{{ 'weightReport.jobCard.jobTitle' | translate }}</label>
        <span>{{ workOrder?.title || '-' }}</span>
      </div>
      <div class="readonly-field">
        <label>{{ 'weightReport.jobCard.platform' | translate }}</label>
        <span>{{ workOrder?.siteNo || '-' }}</span>
      </div>
      <div class="readonly-field">
        <label>{{ 'weightReport.jobCard.performedBy' | translate }}</label>
        <span>{{ currentUserEmail || '-' }}</span>
      </div>
    </div>
  </mat-card>

  <!-- Current schema: unsent lines -->
  <mat-card class="section-card" appearance="outlined">
    <div class="schema-header">
      <mat-card-title class="section-title">{{ 'weightReport.currentSchema.title' | translate }}</mat-card-title>
      @if (!showLineForm) {
        <button mat-flat-button color="primary" type="button" (click)="addLine()">
          <em class="material-icons">add</em>
          {{ 'weightReport.line.add' | translate }}
        </button>
      }
    </div>

    @if (openLines.length === 0) {
      <p class="history-empty">{{ 'weightReport.currentSchema.empty' | translate }}</p>
    } @else {
      <div class="lines-table">
        <table class="lines-table-grid open-table" aria-label="Open weight lines">
          <thead>
            <tr class="lines-head">
              <th>{{ 'weightReport.line.tag' | translate }}</th>
              <th>{{ 'weightReport.line.quantity' | translate }}</th>
              <th>{{ 'weightReport.weight.installed' | translate }} (kg)</th>
              <th>{{ 'weightReport.weight.removed' | translate }} (kg)</th>
              <th>{{ 'weightReport.weight.total' | translate }} (kg)</th>
              <th>{{ 'weightReport.registration.hours' | translate }} (h)</th>
              <th>{{ 'weightReport.line.descriptionColumn' | translate }}</th>
              <th>{{ 'weightReport.line.status' | translate }}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            @for (line of openLines; track line.clientLineId ?? line.id ?? $index) {
              <tr class="lines-row">
                <td [title]="line.areaTagSystem">
                  <span class="line-tag-cell">
                    @if (hasLineAttachments(line.id)) {
                      <button
                        type="button"
                        class="attachment-trigger"
                        [matMenuTriggerFor]="attachmentsMenu"
                        aria-label="View attachments"
                      >
                        <mat-icon class="attachment-indicator">attach_file</mat-icon>
                      </button>
                      <mat-menu #attachmentsMenu="matMenu">
                        @for (attachment of getLineAttachmentsList(line.id); track attachment.id) {
                          <button
                            mat-menu-item
                            type="button"
                            (click)="downloadLineAttachment(openSchema!.id!, line, attachment)"
                          >
                            {{ attachment.fileName }}
                          </button>
                        }
                      </mat-menu>
                    }
                    <span class="truncate-text">{{ line.areaTagSystem || '-' }}</span>
                  </span>
                </td>
                <td>{{ line.quantityMetres || '-' }}</td>
                <td>{{ formatWeight(line.installedWeight) }}</td>
                <td>{{ formatWeight(line.removedWeight) }}</td>
                <td>{{ formatWeight(lineTotalWeight(line)) }}</td>
                <td>{{ formatHours(line.hoursSpent) }}</td>
                <td class="line-description" [title]="line.description">{{ line.description || '-' }}</td>
                <td>
                  <em class="status-chip status-draft">{{ 'weightReport.status.draft' | translate }}</em>
                </td>
                <td class="line-actions">
                  <button mat-icon-button type="button" (click)="editLine(line)" aria-label="Edit line">
                    <mat-icon>edit</mat-icon>
                  </button>
                  <button
                    mat-icon-button
                    color="warn"
                    type="button"
                    (click)="deleteLine(line)"
                    aria-label="Delete line"
                  >
                    <mat-icon>delete</mat-icon>
                  </button>
                </td>
              </tr>
            }
          </tbody>
        </table>
      </div>

      <div class="lines-cards">
        @for (line of openLines; track line.clientLineId ?? line.id ?? $index) {
          <div class="line-card">
            <div class="line-card-header">
              <span class="line-card-tag" [title]="line.areaTagSystem">
                <span class="line-card-tag-text">{{ line.areaTagSystem || '-' }}</span>
                @if (hasLineAttachments(line.id)) {
                  <button
                    type="button"
                    class="attachment-trigger"
                    [matMenuTriggerFor]="attachmentsMenu"
                    aria-label="View attachments"
                  >
                    <mat-icon class="attachment-indicator">attach_file</mat-icon>
                  </button>
                  <mat-menu #attachmentsMenu="matMenu">
                    @for (attachment of getLineAttachmentsList(line.id); track attachment.id) {
                      <button
                        mat-menu-item
                        type="button"
                        (click)="downloadLineAttachment(openSchema!.id!, line, attachment)"
                      >
                        {{ attachment.fileName }}
                      </button>
                    }
                  </mat-menu>
                }
              </span>
              <em class="status-chip status-draft">{{ 'weightReport.status.draft' | translate }}</em>
            </div>
            <div class="line-card-body">
              <div class="line-card-row">
                <span class="line-card-label">{{ 'weightReport.line.quantity' | translate }}</span>
                <span class="line-card-value">{{ line.quantityMetres || '-' }}</span>
              </div>
              <div class="line-card-row">
                <span class="line-card-label">{{ 'weightReport.weight.installed' | translate }} (kg)</span>
                <span class="line-card-value">{{ formatWeight(line.installedWeight) }}</span>
              </div>
              <div class="line-card-row">
                <span class="line-card-label">{{ 'weightReport.weight.removed' | translate }} (kg)</span>
                <span class="line-card-value">{{ formatWeight(line.removedWeight) }}</span>
              </div>
              <div class="line-card-row">
                <span class="line-card-label">{{ 'weightReport.weight.total' | translate }} (kg)</span>
                <span class="line-card-value">{{ formatWeight(lineTotalWeight(line)) }}</span>
              </div>
              <div class="line-card-row">
                <span class="line-card-label">{{ 'weightReport.registration.hours' | translate }} (h)</span>
                <span class="line-card-value">{{ formatHours(line.hoursSpent) }}</span>
              </div>
              @if (line.description) {
                <div class="line-card-row line-card-description">
                  <span class="line-card-label">{{ 'weightReport.line.descriptionColumn' | translate }}</span>
                  <span class="line-card-value truncate-text" [title]="line.description">{{ line.description }}</span>
                </div>
              }
            </div>
            <div class="line-card-actions">
              <button mat-icon-button type="button" (click)="editLine(line)" aria-label="Edit line">
                <mat-icon>edit</mat-icon>
              </button>
              <button mat-icon-button color="warn" type="button" (click)="deleteLine(line)" aria-label="Delete line">
                <mat-icon>delete</mat-icon>
              </button>
            </div>
          </div>
        }
      </div>
    }

    <div class="send-row">
      @if (!isOnline) {
        <p class="send-hint">{{ 'weightReport.send.offlineHint' | translate }}</p>
      } @else if (hasPendingLineChanges) {
        <p class="send-hint">{{ 'weightReport.send.pendingHint' | translate }}</p>
      }
      <button mat-flat-button color="primary" type="button" [disabled]="!canSend" (click)="send()">
        {{ (sending ? 'weightReport.sending' : 'weightReport.send.action') | translate: { count: unsentLineCount } }}
      </button>
    </div>
  </mat-card>

  <!-- Add / edit a line -->
  @if (showLineForm) {
    <form class="weight-report-form" (ngSubmit)="saveLine()">
      <mat-card class="section-card" appearance="outlined">
        <mat-card-title class="section-title">
          {{ (editingLine ? 'weightReport.line.editTitle' : 'weightReport.line.addTitle') | translate }}
        </mat-card-title>

        <div class="field-grid line-editor-grid">
          <mat-form-field appearance="outline" class="field-main-category">
            <mat-label>{{ 'weightReport.registration.mainCategory' | translate }}</mat-label>
            <mat-select name="mainCategory" [(ngModel)]="form.mainCategory">
              <mat-option [value]="mainCategoryEnum.AdditionalWork">
                {{ 'weightReport.category.ADDITIONAL_WORK' | translate }}
              </mat-option>
              <mat-option [value]="mainCategoryEnum.WeightChange">
                {{ 'weightReport.category.WEIGHT_CHANGE' | translate }}
              </mat-option>
              <mat-option [value]="mainCategoryEnum.Other">
                {{ 'weightReport.category.OTHER' | translate }}
              </mat-option>
            </mat-select>
          </mat-form-field>
          <mat-form-field appearance="outline" class="field-tag">
            <mat-label>{{ 'weightReport.line.tag' | translate }}</mat-label>
            <input matInput name="areaTagSystem" [(ngModel)]="form.areaTagSystem" />
          </mat-form-field>
          <mat-form-field appearance="outline" class="field-quantity">
            <mat-label>{{ 'weightReport.line.quantity' | translate }}</mat-label>
            <input matInput name="quantityMetres" [(ngModel)]="form.quantityMetres" />
          </mat-form-field>
          <mat-form-field appearance="outline" class="field-hours">
            <mat-label>{{ 'weightReport.registration.hours' | translate }} (h)</mat-label>
            <input matInput type="number" name="hoursSpent" [(ngModel)]="form.hoursSpent" />
          </mat-form-field>
          <mat-form-field appearance="outline" class="field-installed">
            <mat-label>{{ 'weightReport.weight.installed' | translate }} (kg)</mat-label>
            <input matInput type="number" name="installedWeight" [(ngModel)]="form.installedWeight" />
          </mat-form-field>
          <mat-form-field appearance="outline" class="field-removed">
            <mat-label>{{ 'weightReport.weight.removed' | translate }} (kg)</mat-label>
            <input matInput type="number" name="removedWeight" [(ngModel)]="form.removedWeight" />
          </mat-form-field>
          <div class="readonly-field field-total">
            <label>{{ 'weightReport.weight.total' | translate }}</label>
            <span>{{ totalWeightDisplay }}</span>
          </div>
          <mat-form-field appearance="outline" class="field-follow-up">
            <mat-label>{{ 'weightReport.weight.followUp' | translate }}</mat-label>
            <mat-select name="changeFollowUpRequired" [(ngModel)]="form.changeFollowUpRequired">
              <mat-option [value]="true">{{ 'weightReport.weight.followUpYes' | translate }}</mat-option>
              <mat-option [value]="false">{{ 'weightReport.weight.followUpNo' | translate }}</mat-option>
            </mat-select>
          </mat-form-field>
        </div>

        @if (categories.length > 0) {
          <div class="predefined-options">
            <span class="options-label">{{ 'weightReport.registration.predefinedOptions' | translate }}</span>
            <div class="options-grid">
              @for (category of categories; track category.id) {
                <mat-checkbox [checked]="isCategorySelected(category.id!)" (change)="toggleCategory(category.id!)">
                  {{ category.label }}
                </mat-checkbox>
              }
            </div>
          </div>
        }

        <mat-form-field appearance="outline" class="full-width" floatLabel="always">
          <mat-label>{{ 'weightReport.weight.description' | translate }}</mat-label>
          <textarea
            matInput
            rows="4"
            name="description"
            [(ngModel)]="form.description"
            placeholder="{{ 'weightReport.weight.descriptionPlaceholder' | translate }}"
          ></textarea>
        </mat-form-field>

        <mat-form-field appearance="outline" class="full-width" floatLabel="always">
          <mat-label>{{ 'weightReport.documentation.comment' | translate }}</mat-label>
          <textarea
            matInput
            rows="3"
            name="comment"
            [(ngModel)]="form.comment"
            placeholder="{{ 'weightReport.documentation.commentPlaceholder' | translate }}"
          ></textarea>
        </mat-form-field>

        <div class="report-attachments">
          <label class="attachments-label">{{ 'weightReport.documentation.attachments' | translate }}</label>

          @if (editingLine?.id) {
            @if (canUploadLineAttachments(editingLine)) {
              <input
                type="file"
                multiple
                accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
                (change)="onLineFilesSelected(editingLine!, $event)"
                [disabled]="isUploadingLineAttachments($safeNavigationMigration(editingLine?.id))"
              />
            }

            @if (isLoadingLineAttachments($safeNavigationMigration(editingLine?.id))) {
              <p class="history-empty">{{ 'weightReport.documentation.attachmentsLoading' | translate }}</p>
            } @else if (getLineAttachmentsList($safeNavigationMigration(editingLine?.id)).length === 0) {
              <p class="history-empty">{{ 'weightReport.documentation.noAttachments' | translate }}</p>
            } @else {
              <ul class="attachments-list">
                @for (
                  attachment of getLineAttachmentsList($safeNavigationMigration(editingLine?.id));
                  track attachment.id
                ) {
                  <li>
                    <span>{{ attachment.fileName }} ({{ formatFileSize(attachment.size) }})</span>
                    <button
                      mat-button
                      type="button"
                      (click)="downloadLineAttachment(openSchema!.id!, editingLine!, attachment)"
                    >
                      {{ 'weightReport.documentation.downloadAttachment' | translate }}
                    </button>
                  </li>
                }
              </ul>
            }
          } @else if (isOnline) {
            <input
              type="file"
              multiple
              accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
              (change)="onPendingFilesSelected($event)"
            />

            @if (pendingAttachmentFiles.length === 0) {
              <p class="history-empty">{{ 'weightReport.documentation.noAttachments' | translate }}</p>
            } @else {
              <ul class="attachments-list">
                @for (file of pendingAttachmentFiles; track file.name + file.size) {
                  <li>
                    <span>{{ file.name }} ({{ formatFileSize(file.size) }})</span>
                    <button mat-button color="warn" type="button" (click)="removePendingAttachment(file)">
                      {{ 'weightReport.line.delete' | translate }}
                    </button>
                  </li>
                }
              </ul>
              <p class="send-hint">{{ 'weightReport.documentation.attachmentsPendingHint' | translate }}</p>
            }
          } @else {
            <p class="history-empty">{{ 'weightReport.documentation.attachmentsOfflineHint' | translate }}</p>
          }
        </div>
      </mat-card>

      <div class="actions">
        <button mat-stroked-button type="button" (click)="cancelLineForm()">
          {{ 'weightReport.cancel' | translate }}
        </button>
        <button mat-flat-button color="primary" type="submit" [disabled]="savingLine">
          {{ (savingLine ? 'weightReport.line.saving' : 'weightReport.line.save') | translate }}
        </button>
      </div>
    </form>
  }

  <!-- Previously sent schemas -->
  <mat-card class="section-card history-card" appearance="outlined">
    <mat-card-title class="section-title">{{ 'weightReport.history.title' | translate }}</mat-card-title>

    @if (sentSchemas.length === 0) {
      <p class="history-empty">{{ 'weightReport.history.empty' | translate }}</p>
    } @else {
      <mat-accordion class="history-list" multi>
        @for (schema of sentSchemas; track schema.id) {
          <mat-expansion-panel class="history-panel" (opened)="onSchemaPanelOpened(schema)">
            <mat-expansion-panel-header>
              <mat-panel-title class="history-category">
                <em class="status-chip status-sent">{{ 'weightReport.status.sent' | translate }}</em>
                {{ 'weightReport.history.lineCount' | translate: { count: schema.lines?.length ?? 0 } }}
              </mat-panel-title>
              <mat-panel-description class="history-desc">
                <span class="history-meta">{{ schema.createdBy || '-' }}</span>
                <span class="history-date">{{ schema.sentAt | date: 'short' }}</span>
              </mat-panel-description>
            </mat-expansion-panel-header>

            <div class="lines-table">
              <table class="lines-table-grid sent-table" aria-label="Sent weight lines">
                <thead>
                  <tr class="lines-head">
                    <th>{{ 'weightReport.line.tag' | translate }}</th>
                    <th>{{ 'weightReport.line.quantity' | translate }}</th>
                    <th>{{ 'weightReport.weight.installed' | translate }} (kg)</th>
                    <th>{{ 'weightReport.weight.removed' | translate }} (kg)</th>
                    <th>{{ 'weightReport.weight.total' | translate }} (kg)</th>
                    <th>{{ 'weightReport.registration.hours' | translate }} (h)</th>
                    <th>{{ 'weightReport.line.descriptionColumn' | translate }}</th>
                    <th>{{ 'weightReport.registration.mainCategory' | translate }}</th>
                  </tr>
                </thead>
                <tbody>
                  @for (line of schema.lines ?? []; track line.id ?? $index) {
                    <tr class="lines-row sent-row">
                      <td [title]="line.areaTagSystem">
                        <span class="line-tag-cell">
                          @if (hasLineAttachments(line.id)) {
                            <button
                              type="button"
                              class="attachment-trigger"
                              [matMenuTriggerFor]="attachmentsMenu"
                              aria-label="View attachments"
                            >
                              <mat-icon class="attachment-indicator">attach_file</mat-icon>
                            </button>
                            <mat-menu #attachmentsMenu="matMenu">
                              @for (attachment of getLineAttachmentsList(line.id); track attachment.id) {
                                <button
                                  mat-menu-item
                                  type="button"
                                  (click)="downloadLineAttachment(schema.id!, line, attachment)"
                                >
                                  {{ attachment.fileName }}
                                </button>
                              }
                            </mat-menu>
                          }
                          <span class="truncate-text">{{ line.areaTagSystem || '-' }}</span>
                        </span>
                      </td>
                      <td>{{ line.quantityMetres || '-' }}</td>
                      <td>{{ formatWeight(line.installedWeight) }}</td>
                      <td>{{ formatWeight(line.removedWeight) }}</td>
                      <td>{{ formatWeight(lineTotalWeight(line)) }}</td>
                      <td>{{ formatHours(line.hoursSpent) }}</td>
                      <td>
                        <span class="truncate-text" [title]="line.description">{{ line.description || '-' }}</span>
                      </td>
                      <td>{{ 'weightReport.category.' + line.mainCategory | translate }}</td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>

            <div class="lines-cards">
              @for (line of schema.lines ?? []; track line.id ?? $index) {
                <div class="line-card">
                  <div class="line-card-header">
                    <span class="line-card-tag" [title]="line.areaTagSystem">
                      <span class="line-card-tag-text">{{ line.areaTagSystem || '-' }}</span>
                      @if (hasLineAttachments(line.id)) {
                        <button
                          type="button"
                          class="attachment-trigger"
                          [matMenuTriggerFor]="attachmentsMenu"
                          aria-label="View attachments"
                        >
                          <mat-icon class="attachment-indicator">attach_file</mat-icon>
                        </button>
                        <mat-menu #attachmentsMenu="matMenu">
                          @for (attachment of getLineAttachmentsList(line.id); track attachment.id) {
                            <button
                              mat-menu-item
                              type="button"
                              (click)="downloadLineAttachment(schema.id!, line, attachment)"
                            >
                              {{ attachment.fileName }}
                            </button>
                          }
                        </mat-menu>
                      }
                    </span>
                    <span class="line-card-category">{{
                      'weightReport.category.' + line.mainCategory | translate
                    }}</span>
                  </div>
                  <div class="line-card-body">
                    <div class="line-card-row">
                      <span class="line-card-label">{{ 'weightReport.line.quantity' | translate }}</span>
                      <span class="line-card-value">{{ line.quantityMetres || '-' }}</span>
                    </div>
                    <div class="line-card-row">
                      <span class="line-card-label">{{ 'weightReport.weight.installed' | translate }} (kg)</span>
                      <span class="line-card-value">{{ formatWeight(line.installedWeight) }}</span>
                    </div>
                    <div class="line-card-row">
                      <span class="line-card-label">{{ 'weightReport.weight.removed' | translate }} (kg)</span>
                      <span class="line-card-value">{{ formatWeight(line.removedWeight) }}</span>
                    </div>
                    <div class="line-card-row">
                      <span class="line-card-label">{{ 'weightReport.weight.total' | translate }} (kg)</span>
                      <span class="line-card-value">{{ formatWeight(lineTotalWeight(line)) }}</span>
                    </div>
                    <div class="line-card-row">
                      <span class="line-card-label">{{ 'weightReport.registration.hours' | translate }} (h)</span>
                      <span class="line-card-value">{{ formatHours(line.hoursSpent) }}</span>
                    </div>
                    @if (line.description) {
                      <div class="line-card-row line-card-description">
                        <span class="line-card-label">{{ 'weightReport.line.descriptionColumn' | translate }}</span>
                        <span class="line-card-value truncate-text" [title]="line.description">{{
                          line.description
                        }}</span>
                      </div>
                    }
                  </div>
                </div>
              }
            </div>

            <div class="report-details">
              <div class="readonly-field">
                <label>{{ 'weightReport.documentation.recipient' | translate }}</label>
                <span>{{ schema.recipientEmail || '-' }}</span>
              </div>
              <div class="readonly-field">
                <label>{{ 'weightReport.details.sentAt' | translate }}</label>
                <span>{{
                  schema.sentAt ? (schema.sentAt | date: 'short') : ('weightReport.details.notSent' | translate)
                }}</span>
              </div>
            </div>
          </mat-expansion-panel>
        }
      </mat-accordion>
    }
  </mat-card>
</div>
